import { CENTERCOMPONENTS } from './CenterComponents';
import { ACTIVETAB } from './RightSideComponents';
import { Shapes } from '../../Shapes';
import { Filters } from '../../Filters/Filters';
import { Reports } from '../../Reports/Reports';
import { Events } from '../../Events/Events';
import { Atons } from '../../Atons/Atons';
import { useMapPanelContext } from './MapPanelContext';
import {
  CollapsedTable,
  ReportShipTable,
} from '../../Reports/Table/ReportShipTable';
import React, { useEffect, useMemo, useRef } from 'react';
import {
  MapScale,
  useDrawingContext,
  useOpenLayersContext,
  useResetDrawingContext,
} from '@ais-ui/react-openlayers';
import {
  Badge,
  Button,
  PlacedToastViewport,
  Separator,
  ToastView,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToastContext,
} from '@ais-ui/ui-core';
import { DrawingMenu } from '../DrawingMenu';
import { useAISMapContext } from '../AISMapContext';
import { useReportContext } from '../../Reports/ReportContext';
import { useSession } from '@ais-ui/websockets';
import { LayerControl } from '../LayerControl/LayerControl';
import { useDateTimeFormat } from '../../conversions/useDateFormat';
import { MeasureTool } from '../MeasureTool/MeasureTool';
import { ShipPortSearch } from '../Search/ShipPortSearch';
import { cn, useTimeZoneContext } from '@ais-ui/utils';
import { Replays } from '../../Replay/Replays';
import { Messages } from '../../Messages/Messages';
import { CustomizeReport } from '../../Reports/Customize/CustomizeReport';
import { FilterShips } from '../FilterTargets/FilterShips';
import { ColorTargetsBy } from '../ColorTargetsBy/ColorTargetsBy';
import { ChooseTargetCard } from '../ChooseTargetCard';
import { CollapsedReportDetails } from '../../Reports/CollapsedReportDetails';
import { TargetDetails } from '../../Targets/TargetDetails';
import { Legend } from '../../../components/Legend';
import { GridClasses, useGridClasses } from './useGridClasses';
import { useSettings } from '../../Settings/SettingsProvider';
import { TargetsOnMap } from '../TargetsOnMap/TargetsOnMap';
import { TargetsOnMapTable } from '../TargetsOnMap/TargetsOnMapTable';
import { PortDetails } from '../../Ports/PortDetails';
import { CoverageLegend } from '../Coverage/CoverageLegend';
import { useCoverage } from '../Coverage/CoverageContext';
import { RoutesTable } from '../../Routes/RoutesTable';
import { PiMinus, PiMonitorPlay, PiMouse, PiPlus } from 'react-icons/pi';
import { useReplayContext } from '../../Replay/ReplayContext';
import { useViewMode, ViewMode } from '../../../components/ViewModeProvider';
import { ResizableTable } from './ResizableTable/ResizableTable';
import { useSelectionState } from '../../../components/SelectionStateProvider';
import { useGPSLost } from '../GpsLost/GPSLostContext';
import { MapAtons } from '../../Atons/MapAtons';
import { useLengthUnits } from '../../Units/useLengthUnits';

const CursorPositionBadge = () => {
  const { cursorPosition } = useAISMapContext();
  const { settings } = useSettings();

  if (!settings['ribbonPlugin.settings']?.show_cursor_position) {
    return null;
  }

  if (!cursorPosition) {
    return null;
  }

  return (
    <Badge
      variant="secondary"
      className="bg-card shadow-md text-sm w-fit mt-4 cursor-default hover:bg-card"
    >
      {cursorPosition.lat} {cursorPosition.lon}
    </Badge>
  );
};

const RemoteControlBadge = () => {
  const { settings } = useSettings();

  const isRemoteControlEnabled = useMemo(() => {
    return settings['ribbonPlugin.settings']?.remotecontrol_enabled ?? false;
  }, [settings]);

  if (!isRemoteControlEnabled) {
    return null;
  }

  return (
    <Tooltip>
      <TooltipTrigger className="mt-4 w-fit">
        <Badge
          variant="secondary"
          className="bg-card shadow-md text-sm cursor-default hover:bg-card space-x-2"
        >
          <PiMouse className="h-4 w-4" />
          <p>Remote control active</p>
        </Badge>
      </TooltipTrigger>
      <TooltipContent>
        <p>
          External applications can control this view. Click Settings to
          disable.
        </p>
      </TooltipContent>
    </Tooltip>
  );
};

const MapPanelScale = () => {
  const { settings } = useSettings();

  const { selectedUnit } = useLengthUnits();

  const isMapScaleEnabled = useMemo(() => {
    return settings['ribbonPlugin.settings']?.mapscale_enabled ?? false;
  }, [settings]);

  if (!isMapScaleEnabled) {
    return null;
  }

  return <MapScale unitSystem={selectedUnit}></MapScale>;
};

const MapPanelZoomButtons = () => {
  const { settings } = useSettings();

  const { map } = useOpenLayersContext();

  const isZoomButtonsEnabled = useMemo(() => {
    return settings['ribbonPlugin.settings']?.zoom_buttons_enabled ?? false;
  }, [settings]);

  const handleZoomIn = () => {
    if (!map) {
      return;
    }
    const view = map.getView();
    const currentZoom = view.getZoom();

    if (!currentZoom) {
      return;
    }

    // Animate zoom in
    view.animate({
      zoom: currentZoom + 1,
      duration: 250,
    });
  };

  const handleZoomOut = () => {
    if (!map) {
      return;
    }
    const view = map.getView();
    const currentZoom = view.getZoom();
    if (!currentZoom) {
      return;
    }
    // Animate zoom out
    view.animate({
      zoom: currentZoom - 1,
      duration: 250,
    });
  };

  if (!isZoomButtonsEnabled) {
    return null;
  }

  return (
    <div className="flex flex-col items-center bg-background border rounded-lg shadow-lg">
      <Button
        size="icon"
        variant="ghost"
        className="h-8 w-8"
        onClick={handleZoomIn}
      >
        <PiPlus></PiPlus>
      </Button>
      <Separator className="w-4/6"></Separator>
      <Button
        size="icon"
        variant="ghost"
        className="h-8 w-8"
        onClick={handleZoomOut}
      >
        <PiMinus></PiMinus>
      </Button>
    </div>
  );
};

const CreatedAtBadge = () => {
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const { createdAt } = useAISMapContext();

  if (!createdAt) {
    return null;
  }

  return (
    <Badge
      variant="secondary"
      className="bg-card shadow-md text-sm w-fit mt-4 cursor-default hover:bg-card"
    >
      {formatDateTimeWithTimeZone(createdAt)}
    </Badge>
  );
};

const ReplayTimeBadge = () => {
  const { currentTime } = useReplayContext();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const { viewMode } = useViewMode();

  if (!currentTime || viewMode !== ViewMode.Replay) {
    return null;
  }

  return (
    <Badge
      variant="secondary"
      className="bg-card shadow-md text-sm w-fit mt-4 cursor-default hover:bg-card space-x-2"
    >
      <PiMonitorPlay></PiMonitorPlay>
      <p>{formatDateTimeWithTimeZone(currentTime)}</p>
    </Badge>
  );
};

interface MapPanelTableProps {
  gridClasses: GridClasses;
}

const MapPanelTable = ({ gridClasses }: MapPanelTableProps) => {
  const {
    centerComponent,
    centerComponentData,
    tableExpanded,
    loadingCenterComponentData,
  } = useMapPanelContext();
  const { reportType, reportDetails } = useReportContext();

  if (!tableExpanded) {
    return null;
  }

  return (
    <ResizableTable className={cn(gridClasses.reportTableGrid, 'self-end')}>
      {centerComponent === CENTERCOMPONENTS.SHIPTABLE && (
        <ReportShipTable
          className="w-full h-full"
          shipData={centerComponentData}
          jobId={reportDetails?.info?.['Job ID'] as string}
          reportType={reportType}
          isLoading={loadingCenterComponentData}
        />
      )}
      {centerComponent === CENTERCOMPONENTS.TARGETSONMAP && (
        <TargetsOnMapTable className="w-full h-full" />
      )}
      {centerComponent === CENTERCOMPONENTS.ROUTES && (
        <RoutesTable className="w-full h-full" />
      )}
    </ResizableTable>
  );
};

export const MapPanel = () => {
  const mapPanelRef = useRef<HTMLDivElement>(null);
  const { setToastView } = useToastContext();
  const {
    legend: reportLegend,
    reportType,
    reportDetails,
  } = useReportContext();
  const { showCoverageLegend } = useCoverage();
  const { selectedPorts } = useSelectionState();
  const { timeZone } = useTimeZoneContext();
  const gridClasses = useGridClasses();
  const { dbSession, replaySession } = useSession();
  const { gpsLostLegend } = useGPSLost();

  useEffect(() => {
    // Runs on mount
    setToastView(ToastView.Map);
    // Runs on unmount
    return () => {
      setToastView(ToastView.Normal);
    };
  }, []);
  const resetDrawingContext = useResetDrawingContext();
  const { showDrawPanel } = useDrawingContext();

  const {
    activeTab,
    tabChange,
    setTabChange,
    tableExpanded,
    reportDetailsExpanded,
    centerComponent,
  } = useMapPanelContext();

  // We cannot reset the drawing context from the sidebar, so we have to emit the change through tabChange
  useEffect(() => {
    if (tabChange) {
      resetDrawingContext();
      setTabChange(false);
    }
  }, [resetDrawingContext, tabChange, setTabChange]);

  const rightSideMapPanelClass = `w-[384px] cursor-default row-start-1 row-end-4 col-start-6 col-end-7 pointer-events-auto`;

  return (
    <div
      ref={mapPanelRef}
      className={cn(
        'absolute top-4 right-4 grid w-[calc(100%-2rem)] h-[calc(100%-2rem)] bg-transparent pointer-events-none gap-2'
      )}
      style={gridClasses.mapPanelGrid}
    >
      <ShipPortSearch className="pointer-events-auto row-start-1 col-start-1 h-10" />
      <div
        className={cn(
          gridClasses.layerControllerGrid,
          'flex flex-col space-y-2 h-full justify-end bg-transparent w-fit pointer-events-none'
        )}
      >
        <TargetsOnMap className="pointer-events-auto"></TargetsOnMap>

        <ColorTargetsBy className="pointer-events-auto"></ColorTargetsBy>
        <FilterShips className="pointer-events-auto"></FilterShips>
        <MeasureTool className="pointer-events-auto"></MeasureTool>
        <LayerControl className="pointer-events-auto"></LayerControl>
      </div>

      <div
        className={cn(
          gridClasses.metadataGrid,
          'flex flex-col items-end bg-transparent self-end justify-self-end pointer-events-auto w-fit space-y-2 overflow-hidden'
        )}
      >
        <PlacedToastViewport className="m-0 p-0 space-y-2" />
        {!tableExpanded && <CollapsedTable />}
        {!reportDetailsExpanded && <CollapsedReportDetails />}
        <CustomizeReport
          reportType={reportType}
          report={reportDetails}
          className=""
        ></CustomizeReport>
        {!dbSession && (
          <Badge variant="destructive" className="shadow-md text-sm w-fit mt-4">
            Reports down
          </Badge>
        )}
        {!replaySession && (
          <Badge variant="destructive" className="hadow-md text-sm w-fit mt-4">
            Replay down
          </Badge>
        )}
        {showDrawPanel && <DrawingMenu></DrawingMenu>}
        <ChooseTargetCard></ChooseTargetCard>
        <MapPanelZoomButtons></MapPanelZoomButtons>
        {timeZone && (
          <Badge
            variant="secondary"
            className="bg-card shadow-md text-sm w-fit mt-4 cursor-default hover:bg-card"
          >
            {timeZone}
          </Badge>
        )}
        <MapPanelScale></MapPanelScale>

        <CreatedAtBadge></CreatedAtBadge>

        <ReplayTimeBadge></ReplayTimeBadge>
        <RemoteControlBadge></RemoteControlBadge>
        <CursorPositionBadge></CursorPositionBadge>
      </div>

      {reportLegend && reportLegend.items.length > 0 && (
        <Legend
          legend={reportLegend}
          className={cn(
            gridClasses.reportLegendGrid,
            'justify-self-end cursor-default pointer-events-auto w-fit h-fit'
          )}
        ></Legend>
      )}
      {!reportLegend && gpsLostLegend && (
        <Legend
          legend={gpsLostLegend}
          className={cn(
            gridClasses.reportLegendGrid,
            'justify-self-end cursor-default pointer-events-auto w-fit h-fit'
          )}
        ></Legend>
      )}
      {centerComponent !== CENTERCOMPONENTS.NONE && (
        <MapPanelTable gridClasses={gridClasses}></MapPanelTable>
      )}
      {showCoverageLegend && !reportLegend && (
        <CoverageLegend
          className={cn(
            gridClasses.reportLegendGrid,
            'justify-self-end cursor-default pointer-events-auto w-fit h-fit'
          )}
        />
      )}
      <TargetDetails
        className={cn(
          gridClasses.targetInfoGrid,
          'cursor-default pointer-events-auto'
        )}
      />
      {selectedPorts.length > 0 && (
        <PortDetails
          className={cn(
            gridClasses.portInfoGrid,
            'cursor-default pointer-events-auto'
          )}
        ></PortDetails>
      )}
      {activeTab === ACTIVETAB.SHAPES && (
        <Shapes className={rightSideMapPanelClass} />
      )}
      {activeTab === ACTIVETAB.FILTERS && (
        <Filters className={rightSideMapPanelClass} />
      )}
      {activeTab === ACTIVETAB.REPORTS && (
        <Reports className={rightSideMapPanelClass} />
      )}
      {activeTab === ACTIVETAB.EVENTS && (
        <Events className={rightSideMapPanelClass} />
      )}
      {activeTab === ACTIVETAB.REPLAY && (
        <Replays
          className={cn(
            gridClasses.replayGrid,
            'cursor-default pointer-events-auto self-end justify-self-end'
          )}
        />
      )}
      {activeTab === ACTIVETAB.ATONS && (
        <MapAtons className={rightSideMapPanelClass} />
      )}
      {activeTab === ACTIVETAB.MESSAGES && (
        <Messages className={rightSideMapPanelClass}></Messages>
      )}
    </div>
  );
};
